import React from "react";
import PropTypes from "prop-types";
import styles from "./HistoryOverlay.module.css";

// Ported from support-wwg-store-web (BSM Store ProductEditor). Field
// definitions are injected by the host page: each entry knows how to extract
// its value from a snapshot (dotted `key` into the snapshot object) and render
// it (`format` returns a display string; use \n for multi-line values, CSS
// `white-space: pre-line` wraps them).

// ---- helpers -----------------------------------------------------------------
function getNestedValue(obj, key) {
  return key.split(".").reduce((acc, part) => acc?.[part], obj);
}

function isEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a === "object" && typeof b === "object") {
    const sortKeys = (v) => {
      if (Array.isArray(v)) return v.map(sortKeys);
      if (v && typeof v === "object") {
        return Object.keys(v)
          .sort()
          .reduce((o, k) => {
            o[k] = sortKeys(v[k]);
            return o;
          }, {});
      }
      return v;
    };
    return JSON.stringify(sortKeys(a)) === JSON.stringify(sortKeys(b));
  }
  return false;
}

// ---- component ---------------------------------------------------------------
export function HistoryOverlay({
  baseline,
  current,
  fieldDefs,
  onRestoreField,
  onClose,
  onUndoAll,
}) {
  const diffs = fieldDefs.filter(({ key, format }) => {
    const baseVal = getNestedValue(baseline, key);
    const currVal = getNestedValue(current, key);
    if (isEqual(baseVal, currVal)) return false;
    // Skip cosmetic raw-value differences (e.g. "" vs null) that would render
    // two identical display strings.
    return format(baseVal) !== format(currVal);
  });

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Field change history"
      >
        <div className={styles.header}>
          <h2 className={styles.title}>History</h2>
          <div className={styles.headerActions}>
            {onUndoAll && diffs.length > 0 && (
              <button
                type="button"
                className={styles.undoAllButton}
                onClick={onUndoAll}
              >
                Undo All Changes
              </button>
            )}
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Field</th>
                <th className={styles.th}>Original Value</th>
                <th className={styles.th}>New Saved Value</th>
                <th className={styles.th} />
              </tr>
            </thead>
            <tbody>
              {diffs.length === 0 ? (
                <tr>
                  <td className={styles.empty} colSpan={4}>
                    No changes since last save.
                  </td>
                </tr>
              ) : (
                diffs.map(({ key, label, format }) => {
                  const baseVal = getNestedValue(baseline, key);
                  const currVal = getNestedValue(current, key);
                  return (
                    <tr key={key} className={styles.row}>
                      <td className={styles.td}>{label}</td>
                      <td className={styles.td}>{format(baseVal)}</td>
                      <td className={styles.td}>{format(currVal)}</td>
                      <td className={styles.tdAction}>
                        <button
                          type="button"
                          className={styles.restoreButton}
                          onClick={() => onRestoreField(key, baseVal)}
                        >
                          Restore
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

HistoryOverlay.propTypes = {
  baseline: PropTypes.object.isRequired,
  current: PropTypes.object.isRequired,
  fieldDefs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      format: PropTypes.func.isRequired,
    }),
  ).isRequired,
  onRestoreField: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onUndoAll: PropTypes.func,
};

export default HistoryOverlay;
